import React, { useCallback, useImperativeHandle } from 'react';

import { useStack } from '@hooks/useStack';
import {
  Modal,
  ModalContentLayout,
  ModalContentLayoutProps,
  ModalProps,
} from '@components/Modal';

type Step<K extends string> = ModalContentLayoutProps & {
  key: K;
  icon?: React.ReactNode;
};

export type WizardRef<K> = {
  goTo: (step: K) => void;
  goBack: () => void;
};

type WizardProps<K extends string> = Omit<ModalProps, 'children'> & {
  steps: Step<K>[];
  icon?: React.ReactNode;
  ref_?: React.MutableRefObject<WizardRef<K> | null>;
};

export function Wizard<K extends string>({
  open,
  onRequestClose,
  steps,
  icon: wizardIcon,
  ref_,
  onBackButtonPress,
}: WizardProps<K>): React.ReactElement {
  const stack = useStack<Step<K>>([steps[0]], steps, (a, b) => a.key === b.key);

  const activeStep = stack.top;

  const handleBack = useCallback((): void => {
    stack.pop();
    onBackButtonPress?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stack]);

  useImperativeHandle(
    ref_,
    () => ({
      goTo: (stepKey: K) => {
        const step = steps.find((s) => s.key === stepKey);

        if (step) {
          stack.push(step);
        }
      },
      goBack: handleBack,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleBack, steps],
  );

  return (
    <Modal
      open={open}
      onRequestClose={onRequestClose}
      showBackButton={stack.size > 1}
      onBackButtonPress={handleBack}
      dismissOnBackdropPress={false}>
      {activeStep && (
        <ModalContentLayout
          testID={`step-${activeStep.key}`}
          icon={activeStep.icon ?? wizardIcon}
          title={activeStep.title}
          subtitle={activeStep.subtitle}
          actions={activeStep.actions}
          content={activeStep.content}
        />
      )}
    </Modal>
  );
}
